<script setup lang="ts">
import { reactive, ref } from "vue";
import type { FormInstance, FormRules } from "element-plus";
import { ElMessage } from "element-plus";
import router from "../../router";
import axios from "axios";
import { useAuthStore } from "../../store/authStore";
import { isCompanyAdminFromToken } from "../../utils/authRole";

const store = useAuthStore();
const token = localStorage.getItem("_panda");
axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

const ensureAdminAccess = () => {
  if (!isCompanyAdminFromToken(localStorage.getItem("_panda"))) {
    router.replace("/request");
    return false;
  }
  return true;
};

if (!ensureAdminAccess()) {
  // Redirected in ensureAdminAccess.
}

interface InviteItem {
  key: number;
  name: string;
  email: string;
}
const formError = ref("");
const loading = ref(false);
const ruleFormRef = ref<FormInstance>();
const ruleForm = reactive({
  invites: [{ key: 0, name: "", email: "" }],
  inviteRole: "" as "" | "1" | "2",
});

const rules = reactive<FormRules<typeof ruleForm>>({
  inviteRole: [
    { required: true, message: "Please select a role", trigger: "change" },
  ],
  "invites.0.email": [
    {
      required: true,
      message: "Please enter an email address",
      trigger: "blur",
    },
    {
      type: "email" as const,
      message: "Please enter a valid email",
      trigger: "blur",
    },
  ],
});

const submitForm = (formEl: FormInstance | undefined) => {
  if (!formEl) return;
  formError.value = "";
  formEl.validate((valid) => {
    if (valid) {
      loading.value = true;
      postData();
    } else {
      formError.value = "Please check your inputs and try again.";
    }
  });
};

const postData = async () => {
  if (!ensureAdminAccess()) return;
  try {
    const role = Number(ruleForm.inviteRole);
    const response = await axios.post(
      `${import.meta.env.VITE_ONBOARDING_SERVICE}adduser?token=${store.getToken}`,
      {
        email: ruleForm.invites[0].email,
        name: ruleForm.invites[0].name,
        company_id: store.getUserData.company_id,
        user_type: role,
      },
    );
    loading.value = false;
    if (response.status == 200) {
      if (response.data.status) {
        ElMessage({ message: "Invitation sent successfully", type: "success" });
        router.push("/users");
      } else {
        ElMessage({
          message:
            "Your session has expired. Kindy login to continue enjoying our services",
          type: "error",
        });
        logwat();
      }
    } else {
      ElMessage({ message: "Invitation failed, please retry.", type: "error" });
    }
  } catch {
    loading.value = false;
    formError.value = "Something went wrong, please try again.";
  }
};

const logwat = () => {
  store.setAuthStatus(false);
  store.$reset();
  localStorage.removeItem("_panda");
  router.push("/login");
};
</script>

<template>
  <div class="iu-page">
    <div class="iu-inner">
      <div class="iu-card" v-loading="loading">
        <!-- Header -->
        <div class="iu-card-header">
          <div class="iu-icon-pill">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <line x1="19" y1="8" x2="19" y2="14" />
              <line x1="22" y1="11" x2="16" y2="11" />
            </svg>
          </div>
          <div class="iu-header-text">
            <h1 class="iu-title">Invite a User</h1>
            <p class="iu-subtitle">
              They'll receive an email to join your account
            </p>
          </div>
        </div>

        <div class="iu-error" v-if="formError">{{ formError }}</div>

        <el-form
          ref="ruleFormRef"
          :model="ruleForm"
          :rules="rules"
          label-width="0px"
          status-icon
        >
          <template
            v-for="(invite, index) in ruleForm.invites"
            :key="invite.key"
          >
            <!-- Section 1: Account Information -->
            <div class="iu-section">
              <div class="iu-field">
                <label class="iu-label">
                  Name <span class="iu-optional">Optional</span>
                </label>
                <div class="iu-input-wrap">
                  <input
                    v-model="invite.name"
                    class="iu-input"
                    placeholder="Full name"
                  />
                  <span class="iu-input-icon">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </svg>
                  </span>
                </div>
              </div>

              <el-form-item prop="invites.0.email" class="iu-form-item">
                <div class="iu-field">
                  <label class="iu-label">
                    Email Address <span class="iu-required">*</span>
                  </label>
                  <div class="iu-input-wrap">
                    <input
                      v-model="invite.email"
                      class="iu-input"
                      type="email"
                      placeholder="user@company.com"
                    />
                    <span class="iu-input-icon">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      >
                        <path
                          d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"
                        />
                        <polyline points="22,6 12,13 2,6" />
                      </svg>
                    </span>
                  </div>
                </div>
              </el-form-item>
            </div>


            <!-- Section 2: Access Level -->
            <div class="iu-section iu-section-last">
              <el-form-item prop="inviteRole" class="iu-form-item">
                <div class="iu-field">
                  <label class="iu-label">
                    Role <span class="iu-required">*</span>
                  </label>
                  <div class="iu-input-wrap">
                    <select
                      v-model="ruleForm.inviteRole"
                      class="iu-input iu-select"
                      required
                    >
                      <option disabled value="">-- Select --</option>
                      <option value="1">Normal</option>
                      <option value="2">Admin</option>
                    </select>
                    <span class="iu-input-icon iu-input-icon-static">
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      >
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </span>
                  </div>
                </div>
              </el-form-item>
            </div>
          </template>

          <button
            class="iu-submit-btn"
            @click.prevent="submitForm(ruleFormRef)"
            :disabled="loading"
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <line x1="22" y1="2" x2="11" y2="13" />
              <polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
            <span>{{ loading ? "Sending…" : "Send Invitation" }}</span>
          </button>

          <button class="iu-back-btn" @click.prevent="router.push('/users')">
            ← Back to Users
          </button>
        </el-form>
      </div>
    </div>
  </div>
</template>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&display=swap");

* {
  box-sizing: border-box;
}

.iu-page {
  font-family: "DM Sans", sans-serif;
  background: #f5f7fb;
  min-height: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 40px 20px 56px;
}

.iu-inner {
  width: 100%;
  max-width: 520px;
}

.iu-card {
  background: #ffffff;
  border-radius: 14px;
  border: 1px solid #e6ebf2;
  padding: 28px 30px 26px;
  box-shadow:
    0 1px 2px rgba(15, 23, 42, 0.04),
    0 8px 24px -12px rgba(18, 40, 74, 0.18);
}

.iu-card-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 22px;
}

.iu-icon-pill {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: #eef4ff;
  color: #2563eb;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.iu-header-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.iu-title {
  font-size: 1.2rem;
  font-weight: 700;
  color: #12284a;
  margin: 0;
  letter-spacing: -0.01em;
}

.iu-subtitle {
  font-size: 0.82rem;
  color: #8c97a8;
  margin: 0;
}

.iu-error {
  background: #fef2f2;
  border: 1px solid #fca5a5;
  color: #dc2626;
  border-radius: 8px;
  padding: 9px 12px;
  font-size: 0.8rem;
  font-weight: 500;
  margin-bottom: 16px;
}

.iu-section {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.iu-section-last {
  margin-bottom: 4px;
}

.iu-divider {
  height: 1px;
  background: #eef1f6;
  margin: 18px 0 16px;
}

.iu-field {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.iu-label {
  display: block;
  font-size: 0.78rem;
  font-weight: 600;
  color: #475569;
  margin-bottom: 6px;
  letter-spacing: 0.01em;
}

.iu-optional {
  font-weight: 500;
  color: #94a3b8;
  font-size: 0.72rem;
  margin-left: 4px;
}

.iu-required {
  color: #dc2626;
  margin-left: 2px;
}

.iu-input-wrap {
  position: relative;
  width: 100%;
  display: block;
}

.iu-input {
  width: 100%;
  height: 44px;
  padding: 0 40px 0 14px;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  font-family: "DM Sans", sans-serif;
  font-size: 0.88rem;
  color: #0f172a;
  outline: none;
  background: #ffffff;
  transition:
    border-color 0.15s,
    box-shadow 0.15s;
}

.iu-input:hover {
  border-color: #cbd5e1;
}

.iu-input:focus {
  border-color: #1e3f73;
  box-shadow: 0 0 0 4px rgba(30, 63, 115, 0.08);
}

.iu-input::placeholder {
  color: #9aa6b8;
}

.iu-input-icon {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  pointer-events: none;
}

.iu-input-icon-static {
  color: #64748b;
}

.iu-select {
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
}

.iu-form-item {
  margin-bottom: 0;
  width: 100%;
  display: block;
}

.iu-form-item :deep(.el-form-item__content) {
  width: 100%;
  display: block;
  line-height: 1.4;
}

.iu-form-item :deep(.el-form-item__content) > * {
  width: 100%;
  display: block;
}

.iu-form-item :deep(.ep-form-item__error),
.iu-form-item :deep(.el-form-item__error) {
  padding-left: 2px;
  font-size: 0.72rem;
}

.iu-submit-btn {
  width: 100%;
  height: 46px;
  margin-top: 18px;
  background: linear-gradient(135deg, #12284a 0%, #1e3f73 100%);
  color: #ffffff;
  border: none;
  border-radius: 10px;
  font-family: "DM Sans", sans-serif;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-shadow: 0 4px 12px -4px rgba(18, 40, 74, 0.5);
  transition:
    transform 0.12s ease,
    box-shadow 0.15s ease,
    opacity 0.15s ease;
}

.iu-submit-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 8px 18px -6px rgba(18, 40, 74, 0.55);
}

.iu-submit-btn:active:not(:disabled) {
  transform: translateY(0);
}

.iu-submit-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
  box-shadow: none;
}

.iu-back-btn {
  width: 100%;
  height: 38px;
  margin-top: 8px;
  background: none;
  border: none;
  color: #64748b;
  font-family: "DM Sans", sans-serif;
  font-size: 0.82rem;
  font-weight: 500;
  cursor: pointer;
  transition: color 0.15s;
}

.iu-back-btn:hover {
  color: #12284a;
}

@media (max-width: 520px) {
  .iu-page {
    padding: 24px 14px 40px;
  }
  .iu-card {
    padding: 22px 20px 22px;
  }
  .iu-title {
    font-size: 1.1rem;
  }
}
</style>
